/**
 * Compression client avant upload (annonces / objets trouvés : cible ~30 Ko).
 */

let heic2any: ((opts: { blob: Blob; toType: string; quality: number }) => Promise<Blob | Blob[]>) | null = null;
if (typeof window !== 'undefined') {
  import('heic2any').then((module) => {
    heic2any = module.default;
  });
}

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: 'jpeg' | 'png' | 'webp';
  maintainAspectRatio?: boolean;
}

export interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  dimensions: {
    original: { width: number; height: number };
    compressed: { width: number; height: number };
  };
}

export async function compressImage(file: File, options: CompressionOptions = {}): Promise<CompressionResult> {
  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.8,
    format = 'jpeg',
    maintainAspectRatio = true,
  } = options;

  let fileToProcess = file;
  if (isHeicFile(file)) {
    fileToProcess = await convertHeicToJpeg(file);
  }

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      try {
        const originalWidth = img.width;
        const originalHeight = img.height;

        let { width, height } = calculateDimensions(
          originalWidth,
          originalHeight,
          maxWidth,
          maxHeight,
          maintainAspectRatio
        );

        canvas.width = width;
        canvas.height = height;

        ctx?.drawImage(img, 0, 0, width, height);

        const mimeType = getMimeType(format);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const originalName = file.name.replace(/\.[^/.]+$/, '');
              const extension = getFileExtension(format);
              const compressedFile = new File([blob], `${originalName}.${extension}`, {
                type: mimeType,
                lastModified: Date.now(),
              });

              resolve({
                file: compressedFile,
                originalSize: file.size,
                compressedSize: compressedFile.size,
                compressionRatio: Math.round((1 - compressedFile.size / file.size) * 100),
                dimensions: {
                  original: { width: originalWidth, height: originalHeight },
                  compressed: { width, height },
                },
              });
            } else {
              reject(new Error("Erreur lors de la compression de l'image"));
            }
          },
          mimeType,
          quality
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => reject(new Error("Erreur lors du chargement de l'image"));
    img.src = URL.createObjectURL(fileToProcess);
  });
}

function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number,
  maintainAspectRatio: boolean
): { width: number; height: number } {
  if (!maintainAspectRatio) {
    return {
      width: Math.min(originalWidth, maxWidth),
      height: Math.min(originalHeight, maxHeight),
    };
  }

  let width = originalWidth;
  let height = originalHeight;

  if (width > maxWidth) {
    height = (height * maxWidth) / width;
    width = maxWidth;
  }

  if (height > maxHeight) {
    width = (width * maxHeight) / height;
    height = maxHeight;
  }

  return { width: Math.round(width), height: Math.round(height) };
}

function getMimeType(format: string): string {
  switch (format) {
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'webp':
      return 'image/webp';
    default:
      return 'image/jpeg';
  }
}

function getFileExtension(format: string): string {
  switch (format) {
    case 'jpeg':
      return 'jpg';
    case 'png':
      return 'png';
    case 'webp':
      return 'webp';
    default:
      return 'jpg';
  }
}

export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/') || isHeicFile(file);
}

export function isHeicFile(file: File): boolean {
  const heicTypes = [
    'image/heic',
    'image/heif',
    'image/heic-sequence',
    'image/heif-sequence',
  ];

  return (
    heicTypes.includes(file.type.toLowerCase()) ||
    file.name.toLowerCase().endsWith('.heic') ||
    file.name.toLowerCase().endsWith('.heif')
  );
}

async function convertHeicToJpeg(file: File): Promise<File> {
  if (!heic2any) {
    const module = await import('heic2any');
    heic2any = module.default;
  }

  const convertedBlob = await heic2any!({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.75,
  });

  const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
  const originalName = file.name.replace(/\.[^/.]+$/, '');
  return new File([blob], `${originalName}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

export const COMPRESSION_PRESETS = {
  medium: {
    maxWidth: 800,
    maxHeight: 600,
    quality: 0.8,
    format: 'jpeg' as const,
  },
};

export const ANNOUNCEMENT_IMAGE_TARGET_BYTES = 30 * 1024;

type CompressionTier = { maxEdge: number; quality: number };

export async function compressImageTowardsTargetBytes(
  file: File,
  targetBytes: number = ANNOUNCEMENT_IMAGE_TARGET_BYTES
): Promise<CompressionResult> {
  if (!isImageFile(file)) {
    throw new Error("Le fichier n'est pas une image supportée");
  }

  let source = file;
  if (isHeicFile(file)) {
    source = await convertHeicToJpeg(file);
  }

  const tiers: CompressionTier[] = [
    { maxEdge: 1280, quality: 0.76 },
    { maxEdge: 1280, quality: 0.62 },
    { maxEdge: 1024, quality: 0.68 },
    { maxEdge: 1024, quality: 0.54 },
    { maxEdge: 840, quality: 0.58 },
    { maxEdge: 840, quality: 0.46 },
    { maxEdge: 720, quality: 0.52 },
    { maxEdge: 720, quality: 0.4 },
    { maxEdge: 640, quality: 0.48 },
    { maxEdge: 640, quality: 0.36 },
    { maxEdge: 560, quality: 0.4 },
    { maxEdge: 512, quality: 0.36 },
    { maxEdge: 512, quality: 0.32 },
    { maxEdge: 480, quality: 0.34 },
    { maxEdge: 480, quality: 0.3 },
  ];

  let best: CompressionResult | null = null;
  for (const { maxEdge, quality } of tiers) {
    const result = await compressImage(source, {
      maxWidth: maxEdge,
      maxHeight: maxEdge,
      quality,
      format: 'jpeg',
      maintainAspectRatio: true,
    });
    if (!best || result.compressedSize < best.compressedSize) {
      best = result;
    }
    if (result.compressedSize <= targetBytes) {
      return result;
    }
  }

  if (!best) {
    throw new Error('Compression impossible');
  }

  return best;
}
